import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import PageBanner from "../components/PageBanner";
import SectionIntro from "../components/SectionIntro";
import api from "../lib/api";
import SEO from "../lib/SEO";

const PAGE_SIZE = 12;

export default function GalleryCategory() {
    const { category: categorySlug } = useParams();
    const [category, setCategory] = useState(null);
    const [children, setChildren] = useState([]);
    const [cakes, setCakes] = useState([]);
    const [notFound, setNotFound] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [page, setPage] = useState(1);

    useEffect(() => {
        let cancelled = false;
        setPage(1);
        setCategory(null);
        setChildren([]);
        setCakes([]);
        setNotFound(false);
        setLoaded(false);

        (async () => {
            try {
                const catRes = await api.get(`/categories/${categorySlug}`);
                if (cancelled) return;
                const cat = catRes.data;
                setCategory(cat);

                // Does this category have sub-categories?
                const childRes = await api.get(`/categories?parent=${encodeURIComponent(cat.slug)}`);
                if (cancelled) return;
                const kids = Array.isArray(childRes.data) ? childRes.data : [];
                setChildren(kids);

                // Only fetch cakes when this is a leaf category (no children)
                if (kids.length === 0) {
                    const cakeRes = await api.get(`/cakes?category=${encodeURIComponent(cat.name)}`);
                    if (cancelled) return;
                    setCakes(Array.isArray(cakeRes.data) ? cakeRes.data : []);
                }
            } catch {
                if (!cancelled) setNotFound(true);
            } finally {
                if (!cancelled) setLoaded(true);
            }
        })();

        return () => { cancelled = true; };
    }, [categorySlug]);

    if (notFound) {
        return (
            <main className="pt-32 pb-20 max-w-3xl mx-auto px-4 text-center" data-testid="gallery-category-notfound">
                <h2 className="font-display text-3xl mb-4">Category not found</h2>
                <Link to="/gallery" className="btn-pink mt-4">Back to gallery</Link>
            </main>
        );
    }
    if (!category) {
        return <main className="pt-32 pb-20 text-center text-ink-soft" data-testid="gallery-category-loading">Loading…</main>;
    }

    const hasChildren = children.length > 0;
    const isSubCategory = !!category.parent_slug;
    const backTo = isSubCategory ? `/gallery/${category.parent_slug}` : "/gallery";
    const backLabel = isSubCategory ? "Back" : "All categories";

    const totalPages = Math.max(1, Math.ceil(cakes.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const startIdx = (currentPage - 1) * PAGE_SIZE;
    const pageItems = cakes.slice(startIdx, startIdx + PAGE_SIZE);

    return (
        <main data-testid="gallery-category-page">
            <SEO
                title={`${category.name} Cakes — Cake Central`}
                description={category.description || `Browse our ${category.name.toLowerCase()} cake portfolio and send an enquiry.`}
                image={category.cover_image_url || undefined}
                path={`/gallery/${category.slug}`}
            />

            <PageBanner
                eyebrow="Portfolio"
                title={category.name}
                image={category.cover_image_url || "https://images.unsplash.com/photo-1629576690723-bb3a22ae7126?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"}
            />

            <section className="bg-[#FFF7FB] py-10 md:py-14">
                <div className="px-4 sm:px-6 lg:px-10">
                    <Link
                        to={backTo}
                        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink-soft hover:text-blush mb-6 transition-colors"
                        data-testid="back-to-gallery"
                    >
                        <ArrowLeft size={14} /> {backLabel}
                    </Link>

                    {category.description && (
                        <SectionIntro className="mb-8">{category.description}</SectionIntro>
                    )}

                    {/* ── Sub-category tiles (e.g. Birthday → Female / Male / Kids) ── */}
                    {hasChildren ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 max-w-screen-2xl mx-auto">
                            {children.map((c, i) => (
                                <Link
                                    key={c.id}
                                    to={`/gallery/${c.slug}`}
                                    className="group block relative overflow-hidden card-lift"
                                    data-testid={`gallery-subcategory-${i}`}
                                >
                                    <div className="aspect-[4/5] overflow-hidden bg-blush-pale">
                                        {c.cover_image_url && (
                                            <img
                                                src={c.cover_image_url}
                                                alt={c.name}
                                                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                                                loading="lazy"
                                            />
                                        )}
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                                        <h3 className="font-display text-2xl md:text-3xl leading-tight">{c.name}</h3>
                                        <span className="inline-flex items-center gap-2 mt-3 text-[10px] tracking-[0.3em] uppercase text-blush-pale opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                                            View portfolio <ArrowRight size={12} />
                                        </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <>
                            {/* ── Cake portfolio grid ── */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-3 max-w-screen-2xl mx-auto">
                                {pageItems.map((c, i) => (
                                    <Link
                                        key={c.id}
                                        to={`/gallery/${category.slug}/${c.slug}`}
                                        className="group block relative overflow-hidden card-lift"
                                        data-testid={`gallery-cake-${i}`}
                                    >
                                        <div className="aspect-[4/5] overflow-hidden">
                                            <img
                                                src={c.image_url}
                                                alt={c.name}
                                                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                                                loading="lazy"
                                            />
                                        </div>
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                                        <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                                            <h3 className="font-display text-xl md:text-2xl mb-2">{c.name}</h3>
                                            <p className="text-xs text-white/85 line-clamp-2">{c.short_description}</p>
                                        </div>
                                    </Link>
                                ))}
                            </div>

                            {loaded && cakes.length === 0 && (
                                <p className="text-center text-ink-soft py-20">
                                    New designs coming to this category soon.
                                </p>
                            )}

                            {totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 mt-10" data-testid="gallery-category-pagination">
                                    <button
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="w-10 h-10 flex items-center justify-center border border-[#EAE3DE] bg-white text-ink hover:border-blush disabled:opacity-40 disabled:hover:border-[#EAE3DE]"
                                        aria-label="Previous page"
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                                        <button
                                            key={p}
                                            onClick={() => setPage(p)}
                                            className={`min-w-10 h-10 px-3 text-[11px] tracking-[0.2em] transition-colors ${
                                                currentPage === p
                                                    ? "bg-blush text-white"
                                                    : "bg-white border border-[#EAE3DE] text-ink hover:border-blush"
                                            }`}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="w-10 h-10 flex items-center justify-center border border-[#EAE3DE] bg-white text-ink hover:border-blush disabled:opacity-40 disabled:hover:border-[#EAE3DE]"
                                        aria-label="Next page"
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>
        </main>
    );
}
